import { ApolloProvider, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createBrowserHistory as createHistory } from 'history';
import buildHasuraProvider from 'ra-data-hasura';
import React from 'react';
import { Admin, CustomRoutes, DataProvider, Resource } from 'react-admin';
import { Route } from 'react-router-dom';
import client from './apolloClient';
import { LayoutWrapper } from './components/layout-wrapper';
import { Login } from './pages';
import clients from './pages/clients';
import documents from './pages/form-submissions';
import formTemplates from './pages/form-templates';
import guests from './pages/guests';
import logs from './pages/logs';
import notificationDetails from './pages/notification-details';
import notifications from './pages/notifications';
import prompts from './pages/prompts';
import ResetPasswordPage from './pages/reset-password';
import results from './pages/results';
import settings from './pages/settings';
import subscriptions from './pages/subscriptions';
import userDeviceTokens from './pages/user-device-tokens';
import users from './pages/users';
import { authProvider } from './providers/auth.provider';
import { createCustomDataProvider } from './providers/customDataProvider';
import i18nProvider from './providers/i18n.provider';
import { lightTheme } from './services/theme';
const baseApiUrl = process.env.REACT_APP_GRAPH_API_URL || '';

const httpLink = createHttpLink({
  uri: baseApiUrl,
});

let headersAuth = {};
export const setAuthorizationHeader = (token: string) => {
  headersAuth = {
    authorization: `Bearer ${token}`,
  };
};

const App: React.FC = () => {
  const history = createHistory();
  const [dataProvider, setDataProvider] = React.useState<DataProvider | null>(null);
  const authLinkBuilder = () => {
    return setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          ...headersAuth,
        },
      };
    });
  };
  React.useEffect(() => {
    const buildDataProvider = async () => {
      let token = '';
      try {
        const auth = await authProvider.getIdentity();
        if (auth) {
          token = auth.token;
        }
      } catch (e) {
        token = '';
      }
      setAuthorizationHeader(token);
      const authLink = authLinkBuilder();
      const dataProviderHasura = await buildHasuraProvider({
        clientOptions: {
          link: authLink.concat(httpLink) as any,
        },
      });
      const customDataProvider = createCustomDataProvider(dataProviderHasura);
      setDataProvider(() => customDataProvider);
    };
    buildDataProvider();
  }, []);

  if (!dataProvider) return <p>Loading...</p>;

  return (
    <ApolloProvider client={client}>
      <Admin
        theme={lightTheme}
        layout={LayoutWrapper}
        loginPage={Login}
        dataProvider={dataProvider}
        requireAuth={true}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        history={history}
      >
        <Resource name="guest" {...guests} />
        <Resource name="client" {...clients} />
        <Resource name="user" {...users} />
        <Resource name="subscriptions" {...subscriptions} />
        <Resource name="log" {...logs} />
        <Resource name="form_template" {...formTemplates} />
        <Resource name="user_device_tokens" {...userDeviceTokens} />
        <Resource name="notifications" {...notifications} />
        <Resource name="notification_details" {...notificationDetails} />
        <Resource name="setting" {...settings} />
        <Resource name="form_submission" {...documents} />
        <Resource name="prompt_templates" {...prompts} />
        <Resource name="results" {...results} />

        {/* Custom route no auth */}
        <CustomRoutes noLayout>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </CustomRoutes>
      </Admin>
    </ApolloProvider>
  );
};

export default App;
